/**
 * componentPreviewContext.ts — Tracks whether `ComponentViewer` is currently
 * previewing a component in its modal overlay, independent of the real
 * browser route.
 *
 * WHY THIS EXISTS (2026-10-07):
 * `ComponentViewer` renders an arbitrary registered component by sequence
 * number inside a Dialog, WITHOUT changing `window.location` at all (the
 * host module/tab stays whatever it really is - see its own doc comment:
 * "renders components in isolation with no route/query-string context").
 * When the previewed component fires its own real API calls and one fails,
 * `captureRouteContext()` (routeContext.ts) only knows the HOST route
 * (e.g. module 10335 / Registry Manager), not which component is actually
 * being previewed. That produced several confusing dev-backlog bug reports
 * (tasks 52593/55772/55786/55800/56989) where the failing endpoint belonged
 * to a totally unrelated previewed component (10729, 10359, branding-config)
 * while the report's module/tab fields pointed at the Registry Manager host
 * tab the admin happened to be on - making triage think the HOST tab was
 * broken instead of the PREVIEWED component.
 *
 * This module is a tiny, dependency-free singleton (not Redux) so
 * `captureRouteContext()` - a plain synchronous function called from
 * apiService.ts - can read it without a React/Redux import cycle.
 */

interface ActivePreview {
    seq: number;
    friendlyName: string | null;
}

let activePreview: ActivePreview | null = null;

export function setActivePreview(seq: number, friendlyName?: string | null): void {
    activePreview = { seq, friendlyName: friendlyName ?? null };
}

export function clearActivePreview(): void {
    activePreview = null;
}

export function getActivePreview(): ActivePreview | null {
    return activePreview;
}
